import { FileText } from "lucide-react"
import type { FormEvent } from "react"

import { RichTextEditor } from "@/components/rich-text-editor"
import { Button } from "@/components/ui/button"
import { CardContent, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"

export interface JobFormData {
  title: string
  company_name: string
  category: string
  location_type: string
  apply_url: string
  apply_instructions: string
  description: string
  status: string
}

export interface JobFormProps {
  title: string
  data: JobFormData
  errors: Record<string, string>
  processing?: boolean
  onChange: (partial: Partial<JobFormData>) => void
  onSubmit: (e: FormEvent) => void
  onCancel: () => void
  submitLabel?: string
}

const CATEGORIES = [
  { value: "tech", label: "Tecnología" },
  { value: "gastronomia", label: "Gastronomía" },
  { value: "servicios", label: "Servicios" },
  { value: "administracion", label: "Administración" },
  { value: "ventas", label: "Ventas" },
  { value: "educacion", label: "Educación" },
  { value: "salud", label: "Salud" },
  { value: "construccion", label: "Construcción" },
  { value: "limpieza", label: "Limpieza" },
  { value: "transporte", label: "Transporte" },
]

const LOCATION_TYPES = [
  { value: "remote", label: "Remoto" },
  { value: "hybrid", label: "Híbrido" },
  { value: "onsite", label: "Presencial" },
]

export function JobForm({
  title,
  data,
  errors,
  processing = false,
  onChange,
  onSubmit,
  onCancel,
  submitLabel = "Guardar",
}: JobFormProps) {
  const err = errors || {}

  return (
    <div className="w-full max-w-4xl">
      <CardTitle className="text-foreground flex items-center gap-3 px-4 text-2xl font-bold">
        <FileText className="text-primary h-8 w-8" />
        {title}
      </CardTitle>
      <CardContent className="px-4">
        {Object.keys(err).length > 0 && (
          <div className="alert-error mb-6">
            <div className="mb-2 flex items-center gap-2">
              <div className="bg-status-error h-2 w-2 rounded-full"></div>
              <p className="text-destructive font-semibold">
                Por favor corrige los siguientes errores:
              </p>
            </div>
            <ul className="text-destructive list-disc space-y-1 pl-6 text-sm">
              {Object.entries(err).map(([k, v]) => (
                <li key={k}>{v}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-4">
            <div>
              <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                Título del puesto
              </Label>
              <Input
                value={data.title}
                onChange={(e) => onChange({ title: e.target.value })}
                required
                className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                placeholder="Ej: Desarrollador Backend"
              />
              {err.title && (
                <p className="text-destructive mt-1 text-sm">{err.title}</p>
              )}
            </div>

            <div>
              <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                Empresa
              </Label>
              <Input
                value={data.company_name}
                onChange={(e) => onChange({ company_name: e.target.value })}
                required
                className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                placeholder="Nombre de la empresa"
              />
              {err.company_name && (
                <p className="text-destructive mt-1 text-sm">
                  {err.company_name}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Categoría
                </Label>
                <Select
                  value={data.category}
                  onValueChange={(v) => onChange({ category: v })}
                >
                  <SelectTrigger className="border-border focus:border-primary h-12 border-2 px-3 py-5">
                    <SelectValue placeholder="Seleccionar categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {err.category && (
                  <p className="text-destructive mt-1 text-sm">
                    {err.category}
                  </p>
                )}
              </div>

              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Modalidad
                </Label>
                <Select
                  value={data.location_type}
                  onValueChange={(v) => onChange({ location_type: v })}
                >
                  <SelectTrigger className="border-border focus:border-primary h-12 border-2 px-3 py-5">
                    <SelectValue placeholder="Seleccionar modalidad" />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCATION_TYPES.map((lt) => (
                      <SelectItem key={lt.value} value={lt.value}>
                        {lt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {err.location_type && (
                  <p className="text-destructive mt-1 text-sm">
                    {err.location_type}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Cómo aplicar */}
          <div className="space-y-4">
            <div className="mb-4 flex items-center gap-3">
              <div
                className="bg-primary h-6 w-1 rounded"
                aria-hidden="true"
              ></div>
              <h3 className="text-foreground text-lg font-semibold">
                Cómo aplicar
              </h3>
            </div>

            <div>
              <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                URL para aplicar
              </Label>
              <Input
                type="url"
                value={data.apply_url}
                onChange={(e) => onChange({ apply_url: e.target.value })}
                className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                placeholder="https://empresa.com/apply"
              />
              {err.apply_url && (
                <p className="text-destructive mt-1 text-sm">{err.apply_url}</p>
              )}
            </div>

            <div>
              <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                Instrucciones para aplicar
              </Label>
              <Textarea
                value={data.apply_instructions}
                onChange={(e) =>
                  onChange({ apply_instructions: e.target.value })
                }
                className="border-border focus:border-primary border-2 text-base transition-colors duration-200"
                placeholder="Ej: Enviar CV a rrhh@empresa.com"
                rows={3}
              />
              {err.apply_instructions && (
                <p className="text-destructive mt-1 text-sm">
                  {err.apply_instructions}
                </p>
              )}
            </div>
          </div>

          {/* Descripción */}
          <div className="space-y-4">
            <div className="mb-1 flex items-center gap-3">
              <div
                className="bg-accent h-6 w-1 rounded"
                aria-hidden="true"
              ></div>
              <h3 className="text-foreground flex items-center gap-2 text-lg font-semibold">
                <FileText className="text-primary h-5 w-5" />
                Descripción
              </h3>
            </div>
            <RichTextEditor
              value={data.description}
              onChange={(html) => onChange({ description: html })}
              placeholder="Describí el puesto, requisitos, beneficios..."
              className="text-base"
              minHeight="220px"
            />
            {err.description && (
              <p className="text-destructive mt-1 text-sm">{err.description}</p>
            )}
            {err.base && (
              <p className="text-destructive mt-1 text-sm">{err.base}</p>
            )}
          </div>

          <div className="border-border flex justify-end gap-4 border-t pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="h-9 bg-transparent px-4 text-sm font-medium"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={processing}
              className="h-9 px-4 text-sm font-medium"
            >
              {submitLabel}
            </Button>
          </div>
        </form>
      </CardContent>
    </div>
  )
}
